import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useSalesRepFormView, useSubmitSalesRepForm } from './api.js';

const INPUT_CLASS =
  'mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

function Shell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        {children}
      </div>
    </div>
  );
}

/**
 * Public, token-gated internal info-gathering form for the sales rep (/sr/:token).
 * Submitting appends the notes to the onboarding record and completes the
 * "Sales Rep Info" step.
 */
export function PublicSalesRepPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const viewQuery = useSalesRepFormView(token);
  const submitMutation = useSubmitSalesRepForm(token);

  const [notes, setNotes] = useState('');
  const [done, setDone] = useState(false);

  if (viewQuery.isLoading) {
    return (
      <Shell>
        <p className="text-center text-sm text-slate-500">Loading…</p>
      </Shell>
    );
  }

  const view = viewQuery.data;
  if (viewQuery.isError || !view) {
    return (
      <Shell>
        <div className="text-center">
          <h1 className="text-lg font-semibold text-slate-900">Form link unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">
            This link is invalid or has been disabled. Please contact the onboarding team.
          </p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-6 w-6"
            >
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="mt-3 text-lg font-semibold text-slate-900">Thank you!</h1>
          <p className="mt-2 text-sm text-slate-500">
            Your notes have been attached to the onboarding record. You can close this window.
          </p>
        </div>
      </Shell>
    );
  }

  const error =
    submitMutation.isError && submitMutation.error instanceof Error
      ? submitMutation.error.message
      : null;

  return (
    <Shell>
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-brand-600">
        Catalyst onboarding — internal
      </div>
      <h1 className="text-xl font-semibold text-slate-900">
        Sales rep info — {view.customerName}
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        Opportunity: <span className="font-medium text-slate-700">{view.opportunityName}</span>
        {view.salesRepName ? <> · Sales rep: {view.salesRepName}</> : null}
      </p>

      {view.submitted ? (
        <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Info was already submitted for this onboarding. Submitting again adds another note.
        </p>
      ) : null}

      <label className="mt-6 block text-sm">
        <span className="font-medium text-slate-700">
          What should the delivery team know before starting?
        </span>
        <span className="mt-1 block text-xs font-normal text-slate-500">
          Key stakeholders, commitments made during the sale, scope nuances, risks, timing
          constraints — anything worth flagging. Your answers are saved to the onboarding
          record&apos;s notes.
        </span>
        <textarea
          rows={12}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={INPUT_CLASS}
          placeholder="Type your notes here…"
        />
      </label>

      {error ? (
        <p className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
      ) : null}

      <div className="mt-6 flex justify-end border-t border-slate-100 pt-4">
        <button
          type="button"
          disabled={submitMutation.isPending || !notes.trim()}
          onClick={() =>
            submitMutation.mutate({ notes: notes.trim() }, { onSuccess: () => setDone(true) })
          }
          className="rounded-md bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
        >
          {submitMutation.isPending ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </Shell>
  );
}
